// Dev mode configuration for email forms and CAPTCHA
export const DEV_MODE = {
  // Environment-based dev mode flag
  isEnabled: import.meta.env.DEV && import.meta.env.VITE_DEV_MODE === 'true',
  
  // Mock data for development
  mockData: {
    // Mock CAPTCHA token for development
    captchaToken: 'dev-mode-token',
    
    // Mock email responses
    emailResponses: {
      success: {
        status: 200,
        message: 'Email sent successfully (dev mode)'
      },
      rateLimit: {
        status: 429,
        message: 'Rate limit exceeded (dev mode)'
      },
      validation: {
        status: 422,
        message: 'Validation failed (dev mode)'
      }
    },
    
    // Mock API delay in milliseconds
    apiDelay: 1000
  }
};

// Helper function to simulate API delay in dev mode
export const simulateApiDelay = async () => {
  if (DEV_MODE.isEnabled) {
    await new Promise(resolve => setTimeout(resolve, DEV_MODE.mockData.apiDelay));
  }
};

// Helper function to get CAPTCHA token (real or mock)
export const getCaptchaToken = (token: string | null) => {
  if (DEV_MODE.isEnabled) {
    return DEV_MODE.mockData.captchaToken;
  }
  return token;
};

// Helper function to handle email form submission in dev mode
export const handleDevModeEmailSubmit = async (
  endpoint: string,
  data: any,
  options: {
    onSuccess?: () => void;
    onError?: (error: any) => void;
    onRateLimit?: () => void;
    onValidation?: (message: string) => void;
  } = {}
) => {
  if (!DEV_MODE.isEnabled) {
    return null; // Let the real API handle it
  }

  await simulateApiDelay();

  // Simulate different response scenarios
  const scenarios = [
    { probability: 0.7, response: DEV_MODE.mockData.emailResponses.success },
    { probability: 0.2, response: DEV_MODE.mockData.emailResponses.validation },
    { probability: 0.1, response: DEV_MODE.mockData.emailResponses.rateLimit }
  ];

  const random = Math.random();
  let cumulativeProbability = 0;
  let selectedResponse = scenarios[0].response;

  for (const scenario of scenarios) {
    cumulativeProbability += scenario.probability;
    if (random <= cumulativeProbability) {
      selectedResponse = scenario.response;
      break;
    }
  }

  // Simulate the response
  if (selectedResponse.status === 200) {
    options.onSuccess?.();
    return { ok: true, status: 200 };
  } else if (selectedResponse.status === 429) {
    options.onRateLimit?.();
    return { ok: false, status: 429 };
  } else {
    options.onValidation?.(selectedResponse.message);
    return { ok: false, status: 422 };
  }
};

// Helper function to check if CAPTCHA is required
export const isCaptchaRequired = () => {
  return !DEV_MODE.isEnabled;
};

// Helper function to get CAPTCHA headers
export const getCaptchaHeaders = (token: string | null) => {
  if (DEV_MODE.isEnabled) {
    return {};
  }
  return token ? { 'CF-Turnstile-Token': token } : {};
}; 